import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import {
  useBingoSuperRewardMutation,
  useLazyGetBingoEventQuery,
} from '@/api/queries/bingo';
import { getIcon } from '@/common/lib/getIcon';
import { Playdeck } from '@/common/lib/playdeck';
import {
  H1,
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { claimBoardReward, initFromApi } from '@/redux/reducers/bingoReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const STATIC_IMG: Record<string, string> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  boardStarPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Record<string, 'tgem' | 'dice' | 'star' | 'cash'> = {
  tgem: 'tgem',
  dice: 'dice',
  cash: 'cash',
};

const REWARD_LABELS: Record<string, string> = {
  tgem: 'T-Gems',
  dice: 'Dice',
  starPoints: 'Star Points',
  boardStarPoints: 'Star Points',
  goldTicket: 'Gold Ticket',
  silverTicket: 'Silver Ticket',
  diamondTicket: 'Diamond Ticket',
  bingoBalls: 'Bingo Balls',
};

function getRewardIcon(type: string): string | null {
  if (STATIC_IMG[type]) return STATIC_IMG[type];
  const icon = getIcon(type);

  if (icon) return icon.webp ?? icon.png;

  return null;
}

const BingoCompleteModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [claimSuperReward] = useBingoSuperRewardMutation();
  const [fetchBingoEvent] = useLazyGetBingoEventQuery();
  const boardComplete = useAppSelector((state) => state.bingo.boardComplete);
  const rewardsClaimed = useAppSelector((state) => state.bingo.rewardsClaimed);
  const pendingLineRewards = useAppSelector(
    (state) => state.bingo.pendingLineRewards,
  );
  const superRewardOptions = useAppSelector(
    (state) => state.bingo.superRewardOptions,
  );

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);

  const isVisible =
    boardComplete && !rewardsClaimed && pendingLineRewards.length === 0;

  const handleClaim = async (): Promise<void> => {
    if (selectedIndex === null || isClaiming) return;

    setIsClaiming(true);

    const rewardToBalance: Record<string, string> = {
      cash: 'cash',
      tgem: 'tgem',
      dice: 'dice',
      starPoints: 'point',
      boardStarPoints: 'point',
      bingoBalls: 'bingoBalls',
    };

    try {
      await claimSuperReward({ optionIndex: selectedIndex }).unwrap();

      const reward = superRewardOptions[selectedIndex];

      if (reward) {
        const balanceKey = rewardToBalance[reward.type];

        if (balanceKey) {
          dispatch(addToBalance({ [balanceKey]: reward.value }));
        }

        Playdeck.sendAnalitics({
          type: 'custom_event',
          name: 'bingo_super_reward_claimed',
          user_properties: {},
          event_properties: {
            reward_type: reward.type,
            reward_value: reward.value,
          },
        });
      }

      dispatch(claimBoardReward());

      // Fetch new card after claiming super reward
      const { data: newEvent } = await fetchBingoEvent();

      if (newEvent) {
        dispatch(initFromApi(newEvent));
      }
    } catch {
      showToast('Something went wrong');
    } finally {
      setIsClaiming(false);
      setSelectedIndex(null);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={css.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <div className={css.glowPurple} />

          <motion.div
            className={css.titleWrap}
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.4, ease: 'easeOut' }}
          >
            <H1 weight="black" className={css.title}>
              {t('Bingo Complete!')}
            </H1>
            <StringMain className={css.subtitle}>
              {t('Choose one reward from the chest')}
            </StringMain>
          </motion.div>

          <motion.div
            className={css.chestWrap}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.25,
              duration: 0.5,
              ease: [0.34, 1.56, 0.64, 1],
            }}
          >
            <div className={css.chestGlow} />
            <motion.img
              src="/img/icons/case.png"
              alt="chest"
              className={css.chestImg}
              animate={{ y: [0, -8, 0] }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </motion.div>

          <motion.div
            className={css.rewardsSection}
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.4, ease: 'easeOut' }}
          >
            <StringXSmall weight="bold" className={css.rewardsLabel}>
              {t('Select one reward')}
            </StringXSmall>

            <div className={css.rewardsList}>
              {superRewardOptions.map((reward, idx) => {
                const isSelected = selectedIndex === idx;
                const currencyKind = CURRENCY_MAP[reward.type];
                const imgSrc = !currencyKind
                  ? getRewardIcon(reward.type)
                  : null;

                return (
                  <motion.button
                    key={reward.type}
                    className={cn(
                      css.rewardCard,
                      isSelected && css.rewardCardSelected,
                    )}
                    onClick={() => setSelectedIndex(idx)}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileTap={{ scale: 0.94 }}
                    transition={{
                      delay: 0.55 + idx * 0.1,
                      duration: 0.35,
                      ease: [0.34, 1.56, 0.64, 1],
                    }}
                  >
                    {isSelected && <div className={css.selectedGlow} />}
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={reward.type}
                        style={{ width: 52, height: 52, objectFit: 'contain' }}
                      />
                    ) : currencyKind ? (
                      <CurrencyImage
                        kind={currencyKind}
                        size="52px"
                        imageVariant="large"
                      />
                    ) : null}
                    <H2 weight="bold" className={css.rewardValue}>
                      +{reward.value}
                    </H2>
                    <StringXSmall className={css.rewardKind}>
                      {t(
                        REWARD_LABELS[reward.type] ??
                          reward.type.replace(/_/g, ' '),
                      )}
                    </StringXSmall>
                    {isSelected && (
                      <motion.div
                        className={css.checkmark}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          duration: 0.2,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                      >
                        ✓
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className={css.buttonWrap}
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.35, ease: 'easeOut' }}
          >
            <MainButton
              text={
                selectedIndex !== null
                  ? t('Claim reward')
                  : t('Select a reward')
              }
              inactive={selectedIndex === null}
              disabled={selectedIndex === null || isClaiming}
              onClick={handleClaim}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { BingoCompleteModal };
